<!-- IncidentFormComponent.vue -->
<template>
    <div class="incident-form-container">  
      <!-- Diálogo del formulario -->
      <dialog :open="show" class="incident-dialog">
        <div class="dialog-content">
                    
          <div v-if="formData.id">
            <h2>Editar Incidencia</h2>
          </div>
          <div v-else>
            <h2>Nueva Incidencia</h2>
          </div>
          
          <form @submit.prevent="submitForm">
            <div class="form-group" v-if="formData.id">
              <label class="form-label">ID: {{ formData.id }}</label>
            </div>
            
            <!-- Campo de búsqueda para expendeduría -->
            <div class="form-group">
              <label class="form-label">Expendeduría:</label>
              <search-select
                :reset-trigger="!show"
                :items="expendeduriasResults"
                :loading="loadingExpendedurias"
                :initial-value="expendeduriaInitialValue"
                item-text="name"
                @search="handleExpendeduriaSearch"
                @select="selectExpendeduria"
                placeholder="Buscar expendeduría..."
                ref="expendeduriaSelect"
              />
            </div>
  
            <!-- Campo de búsqueda para máquina -->
            <div class="form-group mt-2">
              <label class="form-label">Máquina:</label>
              <search-select
                :items="maquinasResults"
                :loading="loadingMaquinas"
                :disabled="!formData.id_expendeduria"
                :initial-value="maquinaInitialValue"
                item-text="name"
                @search="handleMaquinaSearch"
                @select="selectMaquina"
                placeholder="Buscar máquina..."
                :key="`maquina-select-${formData.id_expendeduria}`"
                ref="maquinaSelect"
              />
              <small v-if="!formData.id_expendeduria" class="text-muted">
                ⚠️ Seleccione primero una expendeduría
              </small>
            </div>
  
            <div class="row mt-2">
              <!-- Selector de operador -->
              <div class="col">
                <label class="form-label">Operador:</label>
                <select class="form-select form-select-sm p-1" v-model="formData.id_operator" required>
                  <option 
                    v-for="operator in operators"
                    :key="operator.id"
                    :value="operator.id"
                  >
                    {{ operator.name }}
                  </option>
                </select>
              </div>

              <!-- Estado resuelto -->
              <div class="col">
                <div class="row">
                  <div class="form-label">
                    Estado:
                  </div>
                </div>
                <div class="row">
                  <div class="form-check">
                    <input 
                      class="form-check-input"
                      type="checkbox"
                      value=""
                      v-model="formData.is_resolved"
                    />
                    <label class="form-check-label">
                      Incidencia resuelta
                    </label>
                  
                  </div>
                </div>
              </div>
            </div>

            <div class="row mt-3">
              <!-- Campos de cliente -->
              <div class="form-group col">
                <label>Nombre del cliente (opcional):</label>
                <input 
                  v-model="formData.client_name"
                  type="text"
                  maxlength="255"
                />
              </div>
    
              <div class="form-group col">
                <label>Teléfono del cliente (opcional):</label>
                <input
                  v-model="formData.client_phone"
                  type="tel"
                  pattern="[0-9]{9,15}"
                  placeholder="Ej: 654968918"
                />
              </div>
            </div>

            <!-- Descripción -->
            <div class="form-group mt-2">
              <label class="form-label">Descripción:</label>
              <textarea
                v-model="formData.description"
                required
                rows="4"
              ></textarea>
            </div>
  
            <!-- Acciones -->
            <div class="form-actions">
              <button 
                type="button"
                @click="closeDialog"
                class="cancel-btn"
              >
                Cancelar
              </button>
              <button 
                type="submit"
                :disabled="submitting"
                class="btn btn-dark"
              >
                <span v-if="submitting">Guardando...</span>
                <span v-else>Guardar Incidencia</span>
              </button>
            </div>
          </form>
        </div>
      </dialog>
    </div>
  </template>
  
  <script setup lang="ts">
  import { ref, reactive, onMounted, watch, nextTick } from 'vue';
  import { useIncidentStore } from '@/stores/useIncidentStore';
  import { storeToRefs } from 'pinia';
  //import axios from 'axios';
  import _ from 'lodash';
  import entitiesAPI from '@/api/entitiesAPI';
  import usersAPI from '@/api/usersAPI';
  import SearchSelect from './SearchSelect.vue';
  import type { Incident } from '@/types/incidentsTypes';

  const incidentStore = useIncidentStore();
  
  const showDialog = ref(false);
  const submitting = ref(false);
  const operators = ref<Operator[]>([]);

  type SearchSelectInstance = {
    resetInput: () => void;
    // Agrega otros métodos si los necesitas
  };

  const expendeduriaInitialValue = ref('');
  const maquinaInitialValue = ref('');
  const expendeduriaSelect = ref<SearchSelectInstance | null>(null);
  const maquinaSelect = ref<SearchSelectInstance | null>(null);

  const props = defineProps<{
    show: boolean;
    incident?: Incident | null;
  }>();

  const emit = defineEmits<{
    (e: 'close'): void
    (e: 'submitted'): void
  }>();
  
  // Eliminar showDialog y usar prop
  watch(() => props.show, (newVal) => {
    if (!newVal) resetForm();
  });

  // Modificar métodos de cierre y envío
  const closeDialog = () => {
    emit('close');
  };


  // Estado del formulario
  const formData = reactive({
    id: null as number | null,
    id_expendeduria: null as number | null,
    id_maquina: null as number | null,
    id_operator: null as number | null,
    client_name: '',
    client_phone: null as number | null,
    is_resolved: false,
    description: ''
  });
  
  // Lista de operadores
  interface Operator {
    id: number | string;
    name: string;
  }
  /* onMounted(async () => {
    try {
      const response = await usersAPI.getOperators();
      console.log("Response:", response.results)
      operators.value = response.results.map((operator: any) => ({
        id: Number(operator.id),
        name: operator.username
      }));
      console.log(operators)
    } catch (error) {
      console.error('Error buscando operadores:', error);
    }
  }); */

  const operatorsLoaded = ref(false);
  const operatorsError = ref(false);
  const loadingOperators = ref(false);

  watch(() => props.show, async (visible) => {
    if (visible) {
      // Solo resetea si NO estamos editando una incidencia existente
      if (!props.incident) {
        resetForm();
      }
      
      // Cargar operadores solo si no se han cargado antes y no hay error
      if (!operatorsLoaded.value && !operatorsError.value && !loadingOperators.value) {
        await loadOperators();
      }

      if (props.incident) {
        await loadIncidentData(props.incident);
      }

      // Solo resetear los inputs si no estamos editando
      await nextTick();
      if (!props.incident) {
        expendeduriaSelect.value?.resetInput();
        maquinaSelect.value?.resetInput();
      }
    }
  });

  // Búsqueda de expendedurías
  const expendeduriasResults = ref<any[]>([]);
  const loadingExpendedurias = ref(false);
  const handleExpendeduriaSearch = _.debounce(async (query: string) => {
    if (query === "") {
      expendeduriasResults.value = [];
      return
    }
    
    try {
      loadingExpendedurias.value = true;
      const response = await entitiesAPI.searchExpendeduria(query);
      expendeduriasResults.value = response.results.slice(0, 10);
    } catch (error) {
      console.error('Error buscando expendedurías:', error);
    } finally {
      loadingExpendedurias.value = false;
    }
  }, 500);
  
  const selectExpendeduria = (item: any) => {
    formData.id_expendeduria = item.id;
  };
  
  // Búsqueda de máquinas
  const maquinasResults = ref<any[]>([]);
  const loadingMaquinas = ref(false);
  const handleMaquinaSearch = _.debounce(async (query: string) => {
    if (!formData.id_expendeduria) {
      maquinasResults.value = [];
      return;
    }

    if (query) {
      formData.id_maquina = null;
    }

    if (query === "") {
      maquinasResults.value = [];
      return
    } 
    try {
      loadingMaquinas.value = true;
      const response = await entitiesAPI.searchMaquina(query, formData.id_expendeduria)
      maquinasResults.value = response.results.slice(0, 10);
    } catch (error) {
      console.error('Error buscando máquinas:', error);
    } finally {
      loadingMaquinas.value = false;
    }
  }, 500);
  
  const selectMaquina = (item: any) => {
    formData.id_maquina = item.id;
  };
  
  // Envío del formulario
  const submitForm = async () => {
    if (!formData.id_operator) {
      alert('Por favor selecciona un operador');
      return;
    }

    submitting.value = true;
    
    try {
      const payload = { ...formData };

      if (formData.id) {
        // modo edición
        await incidentStore.updateIncident(formData.id, payload)
      } else {
        // modo creación
        await incidentStore.createIncident(payload);
      }

      emit('submitted')
      emit('close')
      resetForm();
    } catch (error) {
      console.error('Error creando o editanto incidencia:', error);
      alert('Error al guardar o editar la incidencia');
    } finally {
      submitting.value = false;
    }
  };
  
  const resetForm = () => {
    formData.id = null;
    formData.id_expendeduria = null;
    formData.id_maquina = null;
    formData.id_operator = null;
    formData.client_name = '';
    formData.client_phone = null;
    formData.is_resolved = false;
    formData.description = '';

    expendeduriasResults.value = [];
    maquinasResults.value = [];

    handleExpendeduriaSearch.cancel();
    handleMaquinaSearch.cancel();

    expendeduriaInitialValue.value = "";
    maquinaInitialValue.value = "";
  };

  watch(() => formData.id_operator, (newVal) => {
    console.log('Operador seleccionado:', newVal);
  });


  watch(() => formData.id_expendeduria, (newVal) => {
    if (newVal) {
      formData.id_maquina = null; // Resetear máquina al cambiar expendeduría
      maquinasResults.value = []; // Limpiar resultados anteriores

      const maquinaInput = document.querySelector('.search-select input[placeholder="Buscar máquina..."]') as HTMLInputElement;

      if (maquinaInput) maquinaInput.value = '';
    }
  });

  watch(() => props.show, async (visible) => {
    if (visible) {
      if (!props.incident) {
        resetForm();
      }
      
      if (operators.value.length === 0) {
        await loadOperators();
      }

      if (props.incident) {
        await loadIncidentData(props.incident);
      }

      // Solo resetear los inputs si no estamos editando
      await nextTick();
      if (!props.incident) {
        expendeduriaSelect.value?.resetInput();
        maquinaSelect.value?.resetInput();
      }

      // Limpiar físicamente los inputs
      /* setTimeout(() => {
        const inputs = document.querySelectorAll('.search-select input');
        inputs.forEach(input => (input as HTMLInputElement).value = '');
      }, 50); */
    }
  });

  /* const loadOperators = async () => {
    try {
      const response = await usersAPI.getOperators();
      operators.value = response.results.map((operator: any) => ({
        id: Number(operator.id),
        name: operator.username || 'Sin nombre' // Manejar casos null
      }));
    } catch (error) {
      console.error('Error cargando operadores:', error);
      alert('Error al cargar operadores');
    }
  }; */

  const loadOperators = async () => {
    loadingOperators.value = true;
    try {
      const response = await usersAPI.getOperators();
      operators.value = response.results.map((operator: any) => ({
        id: Number(operator.id),
        name: operator.username || 'Sin nombre'
      }));
      operatorsLoaded.value = true;
      operatorsError.value = false;
    } catch (error) {
      console.error('Error cargando operadores:', error);
      operatorsError.value = true;
    } finally {
      loadingOperators.value = false;
    }
  };


  const loadIncidentData = async (incident: Incident) => {
    console.log("loadIncident:")
    console.log(incident)
    
    formData.id = incident.id;
    formData.id_expendeduria = incident.id_expendeduria;
    formData.id_maquina = incident.id_maquina;
    formData.id_operator = incident.id_operator;
    formData.client_name = incident.client_name;
    formData.client_phone = incident.client_phone;
    formData.is_resolved = incident.is_resolved;
    formData.description = incident.description;

    console.log("formData.id = " + formData.id)

    // Establecer valores iniciales para los campos de búsqueda
    expendeduriaInitialValue.value = incident.expendeduria_name || '';
    maquinaInitialValue.value = incident.maquina_name || '';

    // Cargar máquinas si tenemos expendeduría
    if (incident.id_expendeduria) {
      try {
        loadingMaquinas.value = true;
        const response = await entitiesAPI.searchMaquina("", incident.id_expendeduria);
        maquinasResults.value = response.results;
      } catch (error) {
        console.error("Error cargando máquinas", error);
      } finally {
        loadingMaquinas.value = false;
      }
    }

    // Forzar la actualización de los componentes SearchSelect
    /* await nextTick();
    if (expendeduriaSelect.value) {
      expendeduriaSelect.value.resetInput();
    }
    if (maquinaSelect.value) {
      maquinaSelect.value.resetInput();
    } */
  }
</script>
  
<style scoped>
  .incident-form-container {
    position: relative;
  }
    
  .incident-dialog {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: min(90vw, 600px);
    background: white;
    border-radius: 8px;
    padding: 2rem;
    box-shadow: 0 0 20px rgba(0,0,0,0.2);
    border: none;

    z-index: 1000;
    background: white;
    animation: dialogEnter 0.3s ease;
  }

  .incident-dialog {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1000;
  }
  
  .dialog-content h2 {
    margin-top: 0;
    color: #333;
    border-bottom: 2px solid #eee;
    padding-bottom: 1rem;
  }
  
  .form-group {
    margin-bottom: 1.5rem;
  }
  
  .form-group label {
    display: block;
    margin-bottom: 0.5rem;
    /* color: #666; */
    font-weight: 500;
  }
  
  input[type="text"],
  input[type="tel"],
  select,
  textarea {
    width: 100%;
    padding: 0.8rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
  }
  
  .checkbox-group {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .checkbox-group input {
    width: auto;
  }
  
  .form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 2rem;
  }
  
  .submit-btn {
    background: #4caf50;
    color: white;
    padding: 0.8rem 1.5rem;
    border: none;
    border-radius: 4px;
    cursor: pointer;
  }
  
  .submit-btn:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
  
  .cancel-btn {
    background: #f44336;
    color: white;
    padding: 0.8rem 1.5rem;
    border: none;
    border-radius: 4px;
    cursor: pointer;
  }

  @keyframes dialogEnter {
    from {
      opacity: 0;
      transform: translate(-50%, -45%);
    }
    to {
      opacity: 1;
      transform: translate(-50%, -50%);
    }
  }

  /* select {
    background: #fff url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E") no-repeat right 0.75rem center/12px;
  } */
  </style>